(() => {
  if (customElements.get('rocketz-rating')) return;
  const number = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const sample = (seed) => {
    let hash = 2166136261;
    for (const char of seed) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
    return (hash >>> 0) / 4294967296;
  };
  const between = (a, b, seed, scale, max) => {
    const low = Math.round(clamp(Math.min(number(a), number(b)), 0, max) * scale);
    const high = Math.round(clamp(Math.max(number(a), number(b)), 0, max) * scale);
    return (low + Math.floor(sample(seed) * (high - low + 1))) / scale;
  };
  class RocketzRating extends HTMLElement {
    connectedCallback() {
      this.refresh = this.update.bind(this);
      document.addEventListener('shopify:section:load', this.refresh);
      document.addEventListener('shopify:section:unload', this.refresh);
      document.addEventListener('shopify:section:reorder', this.refresh);
      this.observer = new MutationObserver(() => {
        const signature = this.sourceSignature();
        if (signature !== this.signature) this.update();
      });
      this.observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-reviewz-rating', 'data-reviewz-count'] });
      this.update();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('shopify:section:load', this.refresh);
      document.removeEventListener('shopify:section:unload', this.refresh);
      document.removeEventListener('shopify:section:reorder', this.refresh);
    }
    source() {
      const id = this.dataset.reviewzId.trim().replace(/^#/, '');
      const section = id ? document.getElementById(id) : document.querySelector('[data-reviewz-rating]');
      return section?.hasAttribute('data-reviewz-rating') ? section : null;
    }
    sourceSignature() {
      const source = this.dataset.source === 'sync' ? this.source() : null;
      return source ? `${source.id}:${source.dataset.reviewzRating}:${source.dataset.reviewzCount}` : '';
    }
    update() {
      const d = this.dataset;
      let rating = number(d.rating), count = number(d.count);
      const source = d.source === 'sync' ? this.source() : null;
      this.signature = this.sourceSignature();
      const missing = d.source === 'sync' && !source;
      this.hidden = missing && d.editor !== 'true';
      this.querySelector('.rr-content').hidden = missing;
      const status = this.querySelector('.rr-status');
      if (status) status.hidden = !missing;
      if (missing) return;
      if (source) {
        rating = number(source.dataset.reviewzRating);
        count = number(source.dataset.reviewzCount);
      } else if (d.source === 'random') {
        rating = between(d.ratingMin, d.ratingMax, `${d.product}:rating`, 10, 5);
        count = between(d.countMin, d.countMax, `${d.product}:count`, 1, Number.MAX_SAFE_INTEGER / 2);
      }
      rating = clamp(rating, 0, 5);
      count = Math.max(0, Math.round(count));
      const locale = document.documentElement.lang || 'en';
      const formattedRating = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(rating);
      const formattedCount = new Intl.NumberFormat(locale).format(count);
      const format = (text) => text.replaceAll('[rating]', formattedRating).replaceAll('[count]', formattedCount);
      this.querySelector('.rr-caption').textContent = format(d.caption);
      this.querySelector('.rr-stars').setAttribute('aria-label', format(d.label));
      this.querySelectorAll('.rr-clip').forEach((star, index) => {
        star.style.width = `${clamp(rating - index, 0, 1) * 100}%`;
      });
    }
  }
  customElements.define('rocketz-rating', RocketzRating);
})();
